import { createContext, useState, useEffect } from 'react';
import ErrorReportModal from '../components/errors/ErrorReportModal';
import { apiClient } from '../api/apiClient';

const ErrorContext = createContext(undefined);

// Flag to track if interceptors have been added
let interceptorsAdded = false;

export function ErrorProvider({ children }) {
  const [errors, setErrorsState] = useState([]);

  const setErrors = (newErrors) => {
    setErrorsState(newErrors);
  };

  const clearErrors = () => {
    setErrorsState([]);
  };

  useEffect(() => {
    if (interceptorsAdded) {
      return;
    }
    interceptorsAdded = true;

    const requestInterceptor = apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token && config.headers) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          const { status, data } = error.response;
          if (status === 401) {
            window.location.href = '/login';
          } else if (status === 403) {
            setErrors([
              {
                row: 0,
                message: 'You do not have permission to perform this action.',
              },
            ]);
          } else if (status >= 500) {
            setErrors([
              {
                row: 0,
                message: 'A server error occurred. Please try again later.',
              },
            ]);
          } else if (status === 400 && data.errors) {
            setErrors(data.errors);
          } else {
            setErrors([
              { row: 0, message: data.message || 'An error occurred.' },
            ]);
          }
        } else if (error.request) {
          setErrors([
            {
              row: 0,
              message: 'No response from the server. Please check your network.',
            },
          ]);
        } else {
          setErrors([
            {
              row: 0,
              message: 'An error occurred while setting up the request.',
            },
          ]);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
      interceptorsAdded = false;
    };
  }, []);

  return (
    <ErrorContext.Provider value={{ errors, setErrors, clearErrors }}>
      {children}
      <ErrorReportModal open={errors.length > 0} onClose={clearErrors} errors={errors} />
    </ErrorContext.Provider>
  );
}

export default ErrorContext;
